/**
 * Global online-presence context (web).
 *
 * Opens ONE WebSocket to the calls worker's /presence endpoint for the current
 * logged-in user and maintains the set of online DIDs from the
 * snapshot/presence-up/presence-down protocol. Guests and logged-out users do
 * not connect. Exposed app-wide via <PresenceProvider> so many consumers share
 * one socket.
 *
 * Mirrors the reconnect-with-backoff pattern from the call room socket
 * (useRoomSocket). The service-auth token (~10 min TTL) is re-minted on every
 * (re)connect, since the socket lives longer than a single token.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {CALLS_SERVICE} from '#/lib/constants'
import {useAgent, useSession} from '#/state/session'
import {resolveCallSession} from '#/screens/Call/cfcalls/callToken'

/** An incoming 1:1 call ring, surfaced app-wide for the IncomingCallDialog. */
export interface IncomingCall {
  fromDid: string
  fromHandle?: string
  roomSlug: string
  ts: number
}

/** A ring is auto-dismissed after this long with no answer. */
const RING_TTL_MS = 30_000

interface PresenceState {
  onlineDids: string[]
  isConnected: boolean
  incomingCall: IncomingCall | null
  clearIncomingCall: () => void
}

const Ctx = createContext<PresenceState>({
  onlineDids: [],
  isConnected: false,
  incomingCall: null,
  clearIncomingCall: () => {},
})

export function useOnlineDids(): PresenceState {
  return useContext(Ctx)
}

/** Incoming-call state + a dismisser, for the IncomingCallDialog. */
export function useIncomingCall(): {
  incomingCall: IncomingCall | null
  clearIncomingCall: () => void
} {
  const {incomingCall, clearIncomingCall} = useContext(Ctx)
  return {incomingCall, clearIncomingCall}
}

const service: string = CALLS_SERVICE
const PRESENCE_WS_BASE = `${service.replace(/^http/, 'ws')}/presence`

export function PresenceProvider({children}: {children: ReactNode}) {
  const {hasSession, currentAccount} = useSession()
  const agent = useAgent()
  const [online, setOnline] = useState<Set<string>>(new Set())
  const [isConnected, setIsConnected] = useState(false)
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const selfDid = currentAccount?.did
  const selfHandle = currentAccount?.handle

  const clearIncomingCall = useCallback(() => setIncomingCall(null), [])

  // Auto-dismiss an unanswered ring after RING_TTL_MS.
  useEffect(() => {
    if (!incomingCall) return
    const elapsed = Date.now() - incomingCall.ts
    const remaining = Math.max(0, RING_TTL_MS - elapsed)
    const id = setTimeout(() => setIncomingCall(null), remaining)
    return () => clearTimeout(id)
  }, [incomingCall])

  useEffect(() => {
    // Logged-in real users only. (A guest has no atproto session here.)
    if (!hasSession || !selfDid) {
      // Clear on the next tick (avoid a synchronous setState in the effect body).
      const id = setTimeout(() => {
        setOnline(new Set())
        setIsConnected(false)
        setIncomingCall(null)
      }, 0)
      return () => clearTimeout(id)
    }

    let cancelled = false
    let attempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    function scheduleReconnect() {
      if (cancelled) return
      const base = Math.min(1000 * 2 ** attempt, 15000)
      const jitter = base * 0.2 * (Math.random() - 0.5)
      attempt += 1
      reconnectTimer = setTimeout(
        () => {
          if (!cancelled) void connect()
        },
        Math.round(base + jitter),
      )
    }

    async function connect() {
      if (cancelled) return
      // Fresh token per connect (service-auth ~10min; socket outlives it).
      let token: string
      try {
        const session = await resolveCallSession({
          agent,
          hasSession: true,
          handle: selfHandle,
        })
        token = await session.getToken()
      } catch {
        scheduleReconnect()
        return
      }
      if (cancelled) return

      let ws: WebSocket
      try {
        ws = new WebSocket(
          `${PRESENCE_WS_BASE}?token=${encodeURIComponent(token)}`,
        )
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws
      let openedOnce = false

      ws.addEventListener('open', () => {
        openedOnce = true
        attempt = 0
        setIsConnected(true)
      })

      ws.addEventListener('message', evt => {
        let msg: {
          type?: string
          online?: string[]
          userId?: string
          fromDid?: string
          fromHandle?: string
          roomSlug?: string
          ts?: number
        }
        try {
          msg = JSON.parse(evt.data)
        } catch {
          return
        }
        if (msg.type === 'snapshot' && Array.isArray(msg.online)) {
          setOnline(new Set(msg.online))
        } else if (msg.type === 'presence-up' && msg.userId) {
          setOnline(prev => {
            const next = new Set(prev)
            next.add(msg.userId!)
            return next
          })
        } else if (msg.type === 'presence-down' && msg.userId) {
          setOnline(prev => {
            const next = new Set(prev)
            next.delete(msg.userId!)
            return next
          })
        } else if (msg.type === 'ring' && msg.fromDid && msg.roomSlug) {
          // A peer is ringing us for a 1:1 call. Single-slot: a new ring
          // replaces any pending one.
          setIncomingCall({
            fromDid: msg.fromDid,
            fromHandle: msg.fromHandle,
            roomSlug: msg.roomSlug,
            ts: typeof msg.ts === 'number' ? msg.ts : Date.now(),
          })
        } else if (msg.type === 'ring-cancel' && msg.roomSlug) {
          // Caller gave up. Only clear if it's the ring we're showing.
          setIncomingCall(prev =>
            prev && prev.roomSlug === msg.roomSlug ? null : prev,
          )
        }
      })

      ws.addEventListener('close', () => {
        setIsConnected(false)
        if (cancelled) return
        // Only retry after a socket that had opened (a rejected handshake, e.g.
        // bad token, would loop; a token refresh happens on the next connect
        // anyway, so still retry with backoff).
        if (openedOnce) {
          scheduleReconnect()
        } else {
          scheduleReconnect()
        }
      })

      ws.addEventListener('error', () => {
        // `close` owns reconnection.
      })
    }

    void connect()

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      const ws = wsRef.current
      try {
        if (ws) ws.close()
      } catch {
        // already closing
      }
      wsRef.current = null
    }
  }, [hasSession, selfDid, selfHandle, agent])

  const onlineDids = useMemo(() => [...online], [online])

  const value = useMemo(
    () => ({onlineDids, isConnected, incomingCall, clearIncomingCall}),
    [onlineDids, isConnected, incomingCall, clearIncomingCall],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
